<template>
  <div>
    <div v-if="isLoadingPage">
      <loader :isShow="isLoadingPage" :textInfo="''"></loader>
    </div>
    <div v-else class="container">
      <div class="room-title-wrapper">
        <div v-if="editing" class="create-room-property-title">
          <h2 class="create-room-property-name">
            Property: {{ titlePropertyName }}
          </h2>
          <h2 class="create-room-property-name">
            Room: {{ collectedData.internal_name }}
          </h2>
        </div>
        <div v-if="editing" @click="toggleChangeLogModal" class="last-edit">
          Last edit was {{ difference }} days ago
        </div>
      </div>
      <div class="create-room-container">
        <nav class="FormHeader">
          <span class="Dot" :class="currentPart === 0 ? 'Green' : 'Grey'" />
          <hr class="Line" />
          <span class="Dot" :class="currentPart === 1 ? 'Green' : 'Grey'" />
          <hr class="Line" />
          <span class="Dot" :class="currentPart === 2 ? 'Green' : 'Grey'" />
          <div class="Descriptors">
            <span
              class="Label description"
              :class="currentPart === 0 ? 'Green' : 'Grey'"
            >
              Description
            </span>
            <span class="Label" :class="currentPart === 1 ? 'Green' : 'Grey'">
              Details
            </span>
            <span
              class="Label pricing"
              :class="currentPart === 2 ? 'Green' : 'Grey'"
            >
              Pricing
            </span>
          </div>
        </nav>

        <section v-if="currentPart == 0" class="create-room-description">
          <div class="create-room-title">Description</div>
          <div class="create-room-description-container-1">
            <div class="room-identity-wrapper">
              <div class="create-room-long-label-input">
                <label>Internal name of the room</label>
                <input
                  :class="{ error: !collectedData.internal_name && !validData }"
                  v-model="collectedData.internal_name"
                  type="text"
                  placeholder="E.g. Room C"
                />
                <div
                  v-if="!collectedData.internal_name && !validData"
                  class="field-error"
                >
                  <span class="field-error-icon">i</span>
                  Enter an internal room name
                </div>
              </div>
              <div class="create-room-long-label-input">
                <label>Channel Manager ID</label>
                <input
                  :class="{ error: !collectedData.uplistingId && !validData }"
                  v-model="collectedData.uplistingId"
                  type="text"
                  placeholder="Channel Manager ID"
                />
                <div
                  v-if="!collectedData.uplistingId && !validData"
                  class="field-error"
                >
                  <span class="field-error-icon">i</span>
                  Enter a Channel Manager ID
                </div>
              </div>
            </div>
            <div class="create-room-long-label-input">
              <label>Marketing name of the Room</label>
              <input
                :class="{ error: !collectedData.marketing_name && !validData }"
                v-model="collectedData.marketing_name"
                type="text"
                placeholder="E.g. Cozy and Modern Room at Awesome Name of House"
              />
              <div
                v-if="!collectedData.marketing_name && !validData"
                class="field-error"
              >
                <span class="field-error-icon">i</span>
                Enter a marketing name
              </div>
            </div>
            <div class="create-room-long-label-select">
              <label>Property</label>
              <select
                :class="{
                  error: !collectedData.selected_property && !validData
                }"
                v-model="collectedData.selected_property"
                @change="getRecommendedPricing()"
              >
                <option selected :value="null">Select Property</option>
                <option
                  v-for="property of properties"
                  :key="'property-' + property.uuid"
                  :value="property.uuid"
                >
                  {{ property.name }}
                </option>
              </select>
              <div
                v-if="!collectedData.selected_property && !validData"
                class="field-error"
              >
                <span class="field-error-icon">i</span>
                Select a property
              </div>
            </div>
          </div>
          <div class="create-room-description-container-2">
            <div class="create-room-small-label-select">
              <label>Room lock</label>
              <select
                :class="{ error: !collectedData.lock && !validData }"
                v-model="collectedData.lock"
              >
                <option selected :value="null">Select Lock</option>
                <option
                  v-for="lock of locks"
                  :key="'lock-' + lock"
                  :value="lock"
                >
                  {{ lock }}
                </option>
              </select>
              <div v-if="!collectedData.lock && !validData" class="field-error">
                <span class="field-error-icon">i</span>
                Select a lock
              </div>
            </div>
            <div
              v-if="collectedData.lock == 'Electronic Code'"
              class="create-room-small-label-input"
            >
              <label> Room Code</label>
              <input
                :class="{ error: !collectedData.code && !validData }"
                type="text"
                placeholder="Placeholder"
                v-model="collectedData.code"
              />
              <div v-if="!collectedData.code && !validData" class="field-error">
                <span class="field-error-icon">i</span>
                Enter a room code
              </div>
            </div>
          </div>
          <div class="create-room-description-container-3">
            <div class="create-room-big-label-input">
              <label>Description</label>
              <textarea
                type="text"
                placeholder="Tell us about this room"
                v-model="collectedData.description"
              />
            </div>
          </div>
        </section>

        <section v-if="currentPart == 1" class="create-room-images">
          <Vue2Dropzone
            id="dropzone"
            :includeStyling="false"
            :options="dropzoneOptions"
            useCustomSlot
            @vdropzone-file-added="fileAdded"
            @vdropzone-thumbnail="thumbnailCreated"
          >
            <div class="create-room-title">Add Photos</div>
            <div class="create-room-subtitle">
              Photos help housemates know the room better
            </div>
            <div
              class="create-room-upload-container"
              :class="{ error: photoError }"
            >
              <button>Upload photos</button>
              <label>Or drag them here</label>
            </div>
          </Vue2Dropzone>
          <div v-if="photoError" class="field-error upload-error">
            <span class="field-error-icon">i</span>
            Add at least one photo to continue
          </div>
          <div id="preview" class="image-preview-container">
            <div v-if="photoPreviews.length > 0" class="image-preview-grid">
              <div
                class="image-preview"
                v-for="(photo, index) in photoPreviews"
                :key="'preview-image-' + index"
              >
                <img :src="photo.src" :alt="photo.alt" />
                <button
                  type="button"
                  class="preview-btn preview-star"
                  :class="{ active: coverPhotoIndex === index }"
                  @click="setCoverPhoto(index)"
                  title="Set as cover photo"
                >
                  <svg
                    viewBox="0 0 12 12"
                    width="12"
                    height="12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 0l1.4 3.1 3.4.3-2.6 2.2.8 3.3L6 7.2 3 8.9l.8-3.3L1.2 3.4l3.4-.3L6 0z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="preview-btn preview-delete"
                  @click="removePhoto(index)"
                  title="Remove photo"
                >
                  <svg
                    viewBox="0 0 10 12"
                    width="10"
                    height="12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M1 2h8V1H6V0H4v1H1v1zm1 0h6l-.5 10h-5L2 2zm2 2v6h1V4H4zm2 0v6h1V4H6z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="pricing-container" v-if="currentPart == 2">
          <div class="pricing-title row">Rent Price</div>
          <div class="pricing-subtitle row">
            <p class="pricing-subtitle">Smart Pricing</p>
            <div
              class="pricing-description-status col-2"
              @click="showConfirmation"
            >
              <img
                v-if="priceStatus"
                src="../../assets/notification_on.svg"
                alt="priceStatus_on"
                class="status"
              />
              <img
                v-else
                src="../../assets/notification_off.svg"
                alt="priceStatus_off"
                class="status"
              />
            </div>
          </div>
          <div class="pricing-description row">
            <div class="pricing-description-text col-2">
              Automatically adjust your price based on demand. Your price stays
              within the range you set, and you can change it any time.
            </div>
          </div>
          <hr class="priceLine" />
          <div v-if="priceStatus" class="pricing-graph-container">
            <div class="pricing-graph row">
              <div class="pricing-minmax col-2">
                <p class="pricing-min-label">Minimum Price</p>
                <input
                  class="pricing-min-input"
                  type="text"
                  placeholder="$"
                  v-model="minPrice"
                  v-on:change="renderData"
                />
                <p class="pricing-minmax-recommendation">
                  Recommendation $ {{ minRecommendation }}
                </p>
              </div>
              <div class="pricing-minmax col-2">
                <p class="pricing-max-label">Maximum Price</p>
                <input
                  class="pricing-max-input"
                  type="text"
                  placeholder="$"
                  v-model="maxPrice"
                  v-on:change="renderData"
                />
                <p class="pricing-minmax-recommendation">
                  Recommendation $ {{ maxRecommendation }}
                </p>
              </div>
              <div class="pricing-minmax col-2">
                <p class="pricing-auto-label">Automatic Discount</p>
                <input
                  class="pricing-auto-input"
                  type="text"
                  placeholder="%"
                  v-model="autoAdjustmentPercentage"
                  v-on:change="renderData"
                />
              </div>
            </div>

            <hr class="priceLine" />
            <div class="pricing-subtitle row">
              <p class="pricing-subtitle">Seasonal Adjustments</p>
              <div
                class="pricing-description-status col-2"
                @click="switchAdjustment"
              >
                <img
                  v-if="adjustment"
                  src="../../assets/notification_on.svg"
                  alt="priceStatus_on"
                  class="status"
                />
                <img
                  v-else
                  src="../../assets/notification_off.svg"
                  class="status"
                  alt="priceStatus_off"
                />
              </div>
            </div>

            <div class="pricing-seasonal-adjustment" v-if="adjustment">
              <seasonal-adjustments-list
                :adjustments="adjustments"
                @createGraph="addAdjustment"
                @editGraph="updateAdjustment"
                @deleteGraph="deleteAdjustment"
              ></seasonal-adjustments-list>
            </div>

            <hr class="priceLine" />

            <div class="row pricing-graph-title">
              <div class="col-2 pricing-subtitle">Historical Graph</div>
              <div class="pricing-subtitle-dates col-2">{{ rangeDates }}</div>
            </div>
            <div class="row pricing-graph-information">
              <img alt="location icon" src="../../assets/location_icon.svg" />
              <p class="pricing-selected-property">
                Room - {{ collectedData.internal_name }}
              </p>

              <div class="pricing-graph-buttons">
                <button class="pricing-button pricing-estimated-rent-button">
                  Estimated rent
                </button>
                <button class="pricing-button pricing-last-year-rent-button">
                  Last Year Rent
                </button>
                <button class="pricing-button pricing-actual-rent-button">
                  Actual Rent
                </button>
                <button class="pricing-button pricing-average-rent-button">
                  Average rent
                </button>
              </div>
            </div>
            <div>
              <div
                class="actual-rent-chart-wrapper"
                @mouseleave="clearHistoricalChartHoverState"
              >
                <line-chart
                  :chart-data="dataCollection"
                  :options="options"
                  style="background-color: #fcfcfc"
                ></line-chart>
                <actual-rent-tooltip
                  :visible="historicalChartTooltip.visible"
                  :mode="historicalChartTooltip.mode"
                  :left="historicalChartTooltip.left"
                  :top="historicalChartTooltip.top"
                  :item="historicalChartTooltip.item"
                  :line-label="historicalChartTooltip.lineLabel"
                  :line-value="historicalChartTooltip.lineValue"
                  :date-label="historicalChartTooltip.dateLabel"
                />
              </div>
            </div>
          </div>

          <div class="pricing-graph row" v-else>
            <div class="pricing-minmax col-2">
              <p class="pricing-min-label">Nightly Rent</p>
              <input
                class="pricing-min-input"
                type="text"
                placeholder="$"
                v-model="price"
              />
            </div>
          </div>
        </section>

        <nav class="create-room-footer-container">
          <button @click="backwards()" class="create-room-back-button">
            &lt; Back
          </button>
          <button
            @click="currentPart !== 2 ? forwards() : createAndLink()"
            class="create-room-next-button"
          >
            {{ navButtonText }}
          </button>
        </nav>
      </div>
      <loader :isShow="isLoading" :textInfo="loadingMessage" />
      <Modal
        id="pricing-change-confirmation-modal"
        class="pricing-change-confirmation-modal"
        v-model="isShowChangePriceConfirmationModal"
        :maskClosable="false"
        :closable="false"
        :footer="null"
        :height="165"
        :width="480"
      >
        <div class="modal-title">Warning</div>
        <div class="modal-content">
          Smart Pricing will affect future pricing of the room in the Airbnb
          listing.
        </div>
        <hr style="margin: 0 24px 16px" />
        <div class="modal-button-wrapper">
          <button class="back-btn" @click="closeModal">Back</button>
          <button class="accept-btn" @click="acceptPriceChange">Accept</button>
        </div>
      </Modal>
      <change-log-modal
        :isShow="isShowChangeLogModal"
        @onClose="toggleChangeLogModal"
      />
    </div>
  </div>
</template>
<script>
import { Modal } from 'ant-design-vue'
import calls from '@/api/calls.js'
import { validateFields } from '@/functions/validation.js'
import LineChart from './LineChart.js'
import SeasonalAdjustmentsList from './SeasonalAdjustmentsList.vue'
import ActualRentTooltip from './ActualRentTooltip.vue'
import {
  HISTORICAL_CHART_BASE_OPTIONS,
  historicalChartHoverMixin
} from './historicalChartHover.js'
import Vue2Dropzone from 'vue2-dropzone'
import firebase from 'firebase'
import Loader from '@/components/Loader.vue'
import ChangeLogModal from './ChangeLogModal.vue'
import moment from 'moment'
import { auth } from '../../Firebase'
import { compressImageForUpload } from '@/functions/imageCompression'

// In-file constants (pricing graph and form options)
const PRICING_MONTHS_BACK = 6
const PRICING_MONTHS_AHEAD = 6
const DROPZONE_OPTIONS = {
  url: 'https://httpbin.org/post',
  autoProcessQueue: false,
  maxFiles: 10,
  acceptedFiles: 'image/jpeg,image/png,image/gif'
}

export default {
  mixins: [historicalChartHoverMixin],
  components: {
    LineChart,
    SeasonalAdjustmentsList,
    ActualRentTooltip,
    Vue2Dropzone,
    Loader,
    ChangeLogModal,
    Modal
  },
  data() {
    return {
      propertiesUuids: [
        'c554a598-9b54-47e5-acca-ac3619dadd30',
        'b7878143-ccd8-48e2-9f08-2b2696a0e61e',
        '9e24949c-9e0f-49e4-a60b-32fbedcb64d5',
        'c650fbaf-e3b2-4282-b826-5b437184c7c6',
        'a18d4070-de0c-46f4-93cc-3652c8979805',
        'a244ef9c-38c1-4450-9be2-f2fe3b06e6d9',
        'b377c56e-1056-4a06-8f79-9f57065398ed'
      ],
      rooms: [],
      difference: 0,
      currentPart: 0,
      properties: [],
      titlePropertyName: null,
      dataCollection: null,
      locks: ['Electronic Code', 'Key Lock'],
      propertyTypes: [
        'Apartment',
        'Bed and breakfast',
        'Bungalow',
        'Cabin',
        'Chalent',
        'Condominium',
        'Cottage',
        'Hostel',
        'House',
        'Mansion',
        'Sutdio',
        'Town Home',
        'Villa'
      ],
      bedroomTypes: ['Single', 'Double', 'Triple'],
      bedroomSizes: ['Single', 'Double', 'Triple'],
      bathroomTypes: ['Full', 'Master', 'Three-Quarter', 'Half', 'Quarter'],
      furnishingsTypes: ['Full', 'No Tv', 'Empty'],
      laundryTypes: ['Personal', 'Communal', 'None'],
      accesibilityTypes: ['Stairs', 'Elevator', 'None'],
      parkingTypes: ['Private', 'Public', 'None'],
      petPolicyTypes: ['Cats', 'Dogs', 'Any', 'None'],
      dynamicPricing: false,
      collectedData: {
        internal_name: null,
        marketing_name: null,
        channel_manager_id: null,
        uplistingId: null,
        selected_property: null,
        lock: null,
        code: '',
        description: '',
        monthy_rent: null,
        number_of_housemates: null,
        roomImage: '',
        amenities: {
          property_type: null,
          bedroom_type: null,
          bedroom_size: null,
          bathroom_type: null,
          furnishing: null,
          laundry: null,
          accesibility: null,
          parking: null,
          pet_policy: null
        },
        link_with_airbnb: false,
        link_with_booking: false,
        share_with_marketplace: false,
        photo: null
      },
      newRoomID: null,
      validData: true,
      invalidFields: [],
      photoError: false,
      roomCreationFailed: false,
      roomLinkingFailed: false,
      navButtonText: 'Next',
      editing: false,
      selectedRoom: null,
      previously_selected_room: null,
      priceStatus: false,
      minRecommendation: 100,
      maxRecommendation: 250,
      adjustment: false,
      averageConstant: 0,

      adjustments: [],
      adjustRequests: [],

      price: '$',
      minPrice: '$',
      maxPrice: '$',
      autoAdjustmentPercentage: '%',
      type: '',
      quantity: '',
      select: '',
      startDate: '',
      endDate: '',

      averagePriceArray: [],
      maxPriceArray: [],
      minPriceArray: [],
      datesPriceArray: [],
      actualRentArray: [],
      lastYearActualRentArray: [],
      averageConstantArray: [],
      rangeDates: null,
      options: HISTORICAL_CHART_BASE_OPTIONS,
      dropzoneOptions: DROPZONE_OPTIONS,
      files: [],
      thumbnails: [],
      imageUrls: [],
      existingPhotos: [],
      coverPhotoIndex: 0,
      imageUrl: '',
      isLoading: false,
      loadingMessage: '',
      lastEdit: 0,
      isShowChangeLogModal: false,
      isShowChangePriceConfirmationModal: false,
      documentTitle: '',
      isLoadingPage: false
    }
  },
  computed: {
    photoPreviews() {
      const existing = (this.existingPhotos || []).filter(Boolean)
      const previews = existing.map((url, i) => ({
        src: url,
        alt: `Room photo ${i + 1}`
      }))
      ;(this.files || []).forEach((file, i) => {
        previews.push({
          src: this.thumbnails[i],
          alt: file.name || `Room photo ${previews.length + 1}`
        })
      })
      if (
        previews.length === 0 &&
        this.collectedData &&
        this.collectedData.roomImage
      ) {
        previews.push({
          src: this.collectedData.roomImage,
          alt: 'Room Image'
        })
      }
      return previews
    }
  },
  methods: {
    findFileIndex(file) {
      if (!file) return -1
      const byRef = this.files.indexOf(file)
      if (byRef !== -1) return byRef
      return this.files.findIndex(
        (f) =>
          f &&
          f.name === file.name &&
          f.size === file.size &&
          f.lastModified === file.lastModified
      )
    },
    async uploadFile(file) {
      this.loadingMessage = 'Uploading Picture'
      this.isLoading = true

      let uuid = ''
      if (this.selectedRoom) uuid = this.selectedRoom.uuid
      else uuid = Math.floor(Math.random() * 90000) + 10000

      const fileName = `${uuid}_${Date.now()}_${Math.floor(
        Math.random() * 90000
      )}.jpg`
      let optimizedFile = file

      try {
        optimizedFile = await compressImageForUpload(file)
      } catch (error) {
        console.error(error.message)
      }

      firebase
        .storage()
        .ref(`${fileName}`)
        .put(optimizedFile)
        .then((snapshot) => {
          snapshot.ref.getDownloadURL().then((url) => {
            this.imageUrl = url
            const idx = this.findFileIndex(file)
            if (idx !== -1) this.$set(this.imageUrls, idx, url)
          })
        })
        .catch((err) => {
          console.error(err)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    showConfirmation() {
      this.$modal.show('pricing-change-confirmation-modal')
      this.isShowChangePriceConfirmationModal = true
    },
    acceptPriceChange() {
      this.switchStatus()
      this.getRecommendedPricing()
      this.closeModal()
    },
    closeModal() {
      this.isShowChangePriceConfirmationModal = false
    },
    fileAdded(file) {
      this.files.push(file)
      this.photoError = false
      this.uploadFile(file)
    },
    setCoverPhoto(index) {
      this.coverPhotoIndex = index
    },
    removePhoto(index) {
      const existing = this.existingPhotos || []
      if (existing.length === 0 && (this.files || []).length === 0) {
        this.removeExistingPhoto()
        this.coverPhotoIndex = 0
        return
      }
      if (index < existing.length) {
        this.existingPhotos.splice(index, 1)
      } else {
        const fileIndex = index - existing.length
        this.files.splice(fileIndex, 1)
        this.thumbnails.splice(fileIndex, 1)
        this.imageUrls.splice(fileIndex, 1)
      }
      const remaining =
        (this.existingPhotos || []).length + (this.files || []).length
      if (index < this.coverPhotoIndex) {
        this.coverPhotoIndex -= 1
      } else if (this.coverPhotoIndex >= remaining) {
        this.coverPhotoIndex = 0
      }
      if (remaining === 0) {
        this.collectedData.roomImage = ''
        this.imageUrl = ''
      }
    },
    removeExistingPhoto() {
      this.collectedData.roomImage = ''
      this.imageUrl = ''
      this.imageUrls = []
      this.existingPhotos = []
    },
    thumbnailCreated(file, dataUrl) {
      const idx = this.findFileIndex(file)
      if (idx === -1) this.thumbnails.push(dataUrl)
      else this.$set(this.thumbnails, idx, dataUrl)
    },
    formatPrice(price) {
      return String(price || '')
        .replace('$', '')
        .replace('%', '')
    },
    applyAdjustmentToPrediction(predictionFormula, adjust, isReversal = false) {
      const sign = isReversal ? -1 : 1
      const qty = parseFloat(adjust.quantity) || 0
      if (adjust.type === 'Increase' && adjust.select === 'Amount')
        return predictionFormula + sign * qty
      if (adjust.type === 'Decrease' && adjust.select === 'Amount')
        return predictionFormula - sign * qty
      if (adjust.type === 'Increase' && adjust.select === 'Percentage')
        return predictionFormula + (sign * qty * predictionFormula) / 100
      if (adjust.type === 'Decrease' && adjust.select === 'Percentage')
        return predictionFormula - (sign * qty * predictionFormula) / 100
      return predictionFormula
    },
    buildAdjustmentRequestBody(payload) {
      const reqBody = JSON.parse(JSON.stringify(payload))
      delete reqBody.start_date
      delete reqBody.end_date
      delete reqBody.uuid
      return reqBody
    },
    normalizeAdjustmentPayload(payload) {
      payload.startDate = payload.start_date ?? payload.startDate
      payload.endDate = payload.end_date ?? payload.endDate
      this.type = payload.type
      this.quantity = payload.quantity
      this.select = payload.select
    },
    calculateDates(date) {
      let startDate = new Date(date[0])
      let endDate = new Date(date[1])
      this.rangeDates = `${startDate.toLocaleDateString(
        'en-US'
      )} - ${endDate.toLocaleDateString('en-US')}`
    },
    switchAdjustment() {
      this.adjustment = !this.adjustment
      this.renderData()
    },
    getDate() {
      const d = new Date()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return [month, day, d.getFullYear()].join('/')
    },
    getPricingDateRange() {
      const startDate = new Date()
      startDate.setMonth(startDate.getMonth() - PRICING_MONTHS_BACK)
      const endDate = new Date()
      endDate.setMonth(endDate.getMonth() + PRICING_MONTHS_AHEAD)
      return { startDate, endDate }
    },
    fillData() {
      let pointRadius = []
      const today = this.getDate()
      this.datesPriceArray.forEach(function (eachDate) {
        if (eachDate == today) {
          pointRadius.push(5)
        } else {
          pointRadius.push(1)
        }
      })

      // Map actual rent [{ date, value }] to values aligned with chart labels by date
      const actualRentByTime = new Map()
      ;(this.actualRentArray || []).forEach((item) => {
        if (item && item.date != null) {
          const t = this.toTimeKey(item.date)
          if (t != null) actualRentByTime.set(t, item)
        }
      })
      this.actualRentByTime = actualRentByTime
      const actualRentData = this.datesPriceArray.map((label) => {
        const t = this.toTimeKey(label)
        if (t == null) return null
        const item = actualRentByTime.get(t)
        return item ? item.value : null
      })

      this.dataCollection = {
        labels: this.datesPriceArray,
        datasets: [
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Estimated Rent',
            fill: false,
            borderColor: '#66B015',
            data: this.averagePriceArray,
            yAxisID: 'y',
            backgroundColor: '#66B015',
            pointRadius: pointRadius
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Last Year Rent',
            fill: false,
            borderColor: '#5987EA',
            data: this.lastYearActualRentArray,
            yAxisID: 'y',
            backgroundColor: '#5987EA',
            pointRadius: 1
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Actual Rent',
            fill: true,
            borderColor: '#0B530E',
            data: actualRentData,
            yAxisID: 'y',
            backgroundColor: '#0B530E',
            pointRadius: 0
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Average Rent',
            fill: false,
            borderColor: '#9F9F9F',
            data: this.averageConstantArray,
            yAxisID: 'y',
            pointRadius: 1
          })
        ]
      }
    },
    getRandomInt() {
      return Math.floor(Math.random() * (50 - 5 + 1)) + 5
    },
    switchStatus() {
      this.priceStatus = !this.priceStatus
      if (this.priceStatus) this.dynamicPricing = true
      else this.dynamicPricing = false
    },
    hasPhoto() {
      return (
        (this.files && this.files.length > 0) ||
        (this.existingPhotos && this.existingPhotos.length > 0) ||
        !!this.collectedData.roomImage ||
        !!this.imageUrl
      )
    },
    roomPhotoFields() {
      const ordered = [...(this.existingPhotos || []).filter(Boolean)]
      const fileCount = (this.files || []).length
      for (let i = 0; i < fileCount; i++) {
        const url = (this.imageUrls || [])[i]
        if (url && !ordered.includes(url)) ordered.push(url)
      }
      if (ordered.length === 0 && this.imageUrl) ordered.push(this.imageUrl)
      if (ordered.length === 0 && this.collectedData.roomImage) {
        ordered.push(this.collectedData.roomImage)
      }

      const slots = [...(this.existingPhotos || [])]
      for (let i = 0; i < fileCount; i++) {
        slots.push((this.imageUrls || [])[i] || '')
      }
      if (slots.length === 0 && this.collectedData.roomImage) {
        slots.push(this.collectedData.roomImage)
      }

      const cover =
        slots[this.coverPhotoIndex] || ordered[0] || this.imageUrl || ''

      return {
        roomImage: cover,
        roomImages: ordered.join(',')
      }
    },
    forwards() {
      if (this.currentPart === 0) {
        if (this.validRoom()) {
          this.currentPart += 1
        }
        return
      }
      if (this.currentPart === 1) {
        if (!this.hasPhoto()) {
          this.photoError = true
          return
        }
        this.photoError = false
        this.currentPart += 1
        if (this.currentPart === 2) {
          this.navButtonText = 'Save room'
        }
      }
    },
    backwards() {
      if (this.currentPart > 0) {
        this.currentPart -= 1
        if (this.currentPart < 3) {
          this.navButtonText = 'Next'
        }
      } else {
        this.$router.replace({ path: '/listings/rooms' })
      }
    },
    validRoom() {
      if (this.collectedData.lock == 'Key Lock') {
        //default code to represent key lock
        this.collectedData.code = '123456789'
      }

      const requiredFields = [
        'internal_name',
        'marketing_name',
        'selected_property',
        'uplistingId',
        'lock'
      ]
      // Room Code is required when lock is Electronic Code (Figma Frame 301 error state)
      if (this.collectedData.lock == 'Electronic Code') {
        requiredFields.push('code')
      }

      const { valid, invalidFields = [] } = validateFields(
        requiredFields,
        this.collectedData
      )
      this.validData = valid
      this.invalidFields = invalidFields
      return valid
    },
    async createRoom() {
      if (this.validRoom() && !this.newRoomID) {
        const photos = this.roomPhotoFields()
        var body = {
          internalName: this.collectedData.internal_name,
          marketingName: this.collectedData.marketing_name,
          uplistingId: this.collectedData.uplistingId,
          //description: this.collectedData.description,
          lockType: this.collectedData.lock,
          code: this.collectedData.code.toString(),
          dynamicPricing: this.dynamicPricing,
          roomImage: photos.roomImage,
          roomImages: photos.roomImages,
          email: auth.currentUser.email ?? '',
          adjustment: this.adjustment
        }

        if (
          this.collectedData.description &&
          this.collectedData.description.length > 0
        )
          body.description = this.collectedData.description

        if (this.dynamicPricing) {
          const minPrice = this.formatPrice(this.minPrice)
          if (minPrice.length > 0) body.minPrice = minPrice

          const maxPrice = this.formatPrice(this.maxPrice)
          if (maxPrice.length > 0) body.maxPrice = maxPrice

          const autoAdjustmentPercentage = this.formatPrice(
            this.autoAdjustmentPercentage
          )
          if (autoAdjustmentPercentage.length > 0)
            body.autoAdjustmentPercentage = autoAdjustmentPercentage
        } else {
          const price = this.formatPrice(this.price)
          if (price.length > 0) body.price = price
        }

        const { message, data, roomImageOld } = this.editing
          ? await calls.PUT('rooms', this.selectedRoom.uuid, body)
          : await calls.POST('rooms', body)
        if (message) {
          this.$notify({
            group: 'rent-mgt-notification',
            type: 'error',
            text: message
          })
          this.roomCreationFailed = true
        } else {
          this.newRoomID = data.uuid
          this.roomCreationFailed = false

          // Since we use uuid as the file name, so it will replace the old one.
          // if (this.editing && roomImageOld != '') {
          //   firebase
          //     .storage()
          //     .refFromURL(roomImageOld)
          //     .delete()
          //     .then()
          //     .catch((err) => {
          //       console.error(err)
          //     })
          // }
        }
      }
    },
    async linkRoom() {
      if (this.editing) {
        await calls.DELETE(
          `properties/remove/${this.previously_selected_room}/${this.selectedRoom.uuid}`
        )
        const { message } = await calls.PUT(
          'properties/add',
          `${this.collectedData.selected_property}/${this.selectedRoom.uuid}`
        )
        if (message) {
          this.$notify({
            group: 'rent-mgt-notification',
            type: 'error',
            text: message
          })
        }
      } else {
        if (this.newRoomID) {
          const { message } = await calls.PUT(
            'properties/add',
            `${this.collectedData.selected_property}/${this.newRoomID}`
          )
          if (message) {
            this.$notify({
              group: 'rent-mgt-notification',
              type: 'error',
              text: message
            })
            this.roomLinkingFailed = true
          } else {
            this.roomLinkingFailed = false
          }
        } else {
          this.roomLinkingFailed = true
        }
      }
    },
    async doAdjustmentRequests() {
      // Call all adjustments requests
      for (let i = 0; i < this.adjustRequests.length; i++) {
        const request = this.adjustRequests[i]

        if (request.type == 'POST') {
          request.body.roomId = this.selectedRoom.uuid
          request.body.email = auth.currentUser.email ?? ''
          await calls.POST('adjustment', request.body)
        }
        if (request.type == 'PUT') {
          request.body.roomId = this.selectedRoom.uuid
          request.body.email = auth.currentUser.email ?? ''
          await calls.PUT('adjustment', request.id, request.body)
        }
        if (request.type == 'DELETE') {
          await calls.DELETE_WITH_ROOM_ID_AND_EMAIL(
            'adjustment',
            request.id,
            this.selectedRoom.uuid,
            auth.currentUser.email ?? ''
          )
        }
      }
    },
    async setPrices() {
      if (!this.dynamicPricing || !this.selectedRoom) return

      const minPrice = this.formatPrice(this.minPrice)
      const maxPrice = this.formatPrice(this.maxPrice)
      const autoAdjustmentPercentage = this.formatPrice(
        this.autoAdjustmentPercentage
      )

      if (
        minPrice.length <= 0 ||
        maxPrice.length <= 0 ||
        autoAdjustmentPercentage.length <= 0
      )
        return

      const total = this.averagePriceArray.length
      const body = {
        uuid: this.selectedRoom.uuid,
        total,
        dates: this.datesPriceArray,
        avgs: this.averagePriceArray
      }

      const { message } = await calls.POST('rooms/setPricing', body)
      if (message && this.propertiesUuids.includes(this.selectedRoom.uuid)) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      }
    },
    async createAndLink() {
      this.rooms = this.$route.params.rooms
      this.loadingMessage = 'Saving Room'
      this.isLoading = true
      await this.createRoom()
      if (this.roomCreationFailed == false) {
        await this.linkRoom()
        if (this.roomLinkingFailed == false) {
          await this.doAdjustmentRequests()
          await this.setPrices()
          this.$router.replace({
            path: '/listings/rooms',
            query: { propertyId: this.collectedData.selected_property }
          })
        } else {
          await calls.DELETE('rooms', this.newRoomID)
        }
      }
      this.isLoading = false
    },
    async getRecommendedPricing() {
      try {
        const roomName = encodeURIComponent(
          this.collectedData.selected_property
        )
        const data = await fetch(
          `https://komodo.rentalios.com/min_max_recommendation?property_id=${roomName}`
        )
        const json = await data.json()
        this.minRecommendation = Math.round(json.min_price)
        this.maxRecommendation = Math.round(json.max_price)
      } catch (error) {
        console.error(error)
      }
    },
    async loadProperties() {
      const { data } = await calls.GET('properties')
      this.properties = data
    },
    async getSeasonalPricing(adjustment) {
      let fixedMinPrice = parseInt(this.formatPrice(this.minPrice))
      let fixedMaxPrice = parseInt(this.formatPrice(this.maxPrice))
      fixedMinPrice = Math.round(fixedMinPrice)
      fixedMaxPrice = Math.round(fixedMaxPrice)

      // Get dynamic pricing for seasonal adjustment
      const { data } = await calls.GETPRICING(
        parseInt(fixedMinPrice),
        parseInt(fixedMaxPrice),
        adjustment.startDate,
        adjustment.endDate,
        this.collectedData.selected_property,
        this.selectedRoom.uuid,
        // For realtime update on FE
        // this.adjustment
        !adjustment.editedInFront
      )
      return { seasonDates: data.date, seasonPrices: data.Avg }
    },
    addAdjustment(payload) {
      this.normalizeAdjustmentPayload(payload)
      this.adjustments.push(payload)
      this.adjustRequests.push({
        type: 'POST',
        body: this.buildAdjustmentRequestBody(payload),
        id: payload.uuid
      })
      this.renderData()
    },
    updateAdjustment(payload) {
      this.normalizeAdjustmentPayload(payload)
      const adjIndex = this.adjustments.findIndex(
        (a) => a.uuid === payload.uuid
      )
      if (adjIndex !== -1) {
        this.adjustments[adjIndex] = payload
        this.adjustments[adjIndex].editedInFront = true
      }
      const reqIndex = this.adjustRequests.findIndex(
        (r) => r.id === payload.uuid
      )
      if (reqIndex !== -1) {
        if (this.adjustRequests[reqIndex].type === 'POST') {
          this.adjustRequests[reqIndex].body =
            this.buildAdjustmentRequestBody(payload)
        } else {
          this.adjustRequests.splice(reqIndex, 1)
          this.adjustRequests.push({
            type: 'PUT',
            body: this.buildAdjustmentRequestBody(payload),
            id: payload.uuid
          })
        }
      } else {
        this.adjustRequests.push({
          type: 'PUT',
          body: this.buildAdjustmentRequestBody(payload),
          id: payload.uuid
        })
      }
      this.renderData()
    },
    deleteAdjustment(id) {
      const reqIndex = this.adjustRequests.findIndex((r) => r.id === id)
      if (reqIndex !== -1) this.adjustRequests.splice(reqIndex, 1)
      if (!id.includes('create'))
        this.adjustRequests.push({ type: 'DELETE', id })
      this.renderData(id)
    },
    async renderData(adjusmentIdDeleted = null) {
      this._renderDataId = (this._renderDataId || 0) + 1
      const renderId = this._renderDataId

      this.averagePriceArray = []
      this.minPriceArray = []
      this.maxPriceArray = []
      this.datesPriceArray = []
      this.lastYearActualRentArray = []
      this.averageConstantArray = []

      const { startDate, endDate } = this.getPricingDateRange()
      this.calculateDates([startDate, endDate])

      const minPrice = this.formatPrice(this.minPrice)
      const maxPrice = this.formatPrice(this.maxPrice)
      const autoAdjustmentPercentage = this.formatPrice(
        this.autoAdjustmentPercentage
      )

      if (
        minPrice.length <= 0 ||
        maxPrice.length <= 0 ||
        autoAdjustmentPercentage.length <= 0
      )
        return

      await new Promise((r) => setTimeout(r, 500))
      if (renderId !== this._renderDataId) return

      // Get dynamic pricing for all dates (without seasonal adjustments)
      const { data } = await calls.GETPRICING(
        this.formatPrice(this.minPrice),
        this.formatPrice(this.maxPrice),
        startDate.toLocaleDateString('en-US'),
        endDate.toLocaleDateString('en-US'),
        this.collectedData.selected_property,
        this.selectedRoom.uuid,
        this.adjustment
      )
      if (renderId !== this._renderDataId) return

      // Show the actual rent's price on graph
      await this.getActualRent()
      if (renderId !== this._renderDataId) return

      const lastYearRent = data.LastYearActualRent || []
      for (let i = 0; i < data.Avg.length; i++) {
        this.datesPriceArray.push(data.date[i])

        let predictionFormula = data.Avg[i]
        predictionFormula = Math.round(predictionFormula * 100) / 100
        if (predictionFormula > maxPrice) predictionFormula = maxPrice
        else if (predictionFormula < minPrice) predictionFormula = minPrice

        this.averagePriceArray.push(predictionFormula)
        const lastYearVal = lastYearRent[i]
        this.lastYearActualRentArray.push(
          lastYearVal != null
            ? Math.round(Number(lastYearVal) * 100) / 100
            : null
        )
      }

      // Seasonal adjustments
      if (this.adjustment) {
        for (const adjustIndex in this.adjustments) {
          const adjust = this.adjustments[adjustIndex]
          // Get pricing for this seasonal adjustment
          let { seasonDates, seasonPrices } = await this.getSeasonalPricing(
            adjust
          )
          if (renderId !== this._renderDataId) return
          for (let i = 0; i <= seasonDates.length - 1; i++) {
            const dateIndex = this.datesPriceArray.indexOf(seasonDates[i])
            let predictionFormula = seasonPrices[i]
            if (adjust.editedInFront)
              predictionFormula = this.applyAdjustmentToPrediction(
                predictionFormula,
                adjust,
                false
              )
            if (adjusmentIdDeleted && adjust.uuid === adjusmentIdDeleted)
              predictionFormula = this.applyAdjustmentToPrediction(
                predictionFormula,
                adjust,
                true
              )
            predictionFormula = Math.round(predictionFormula * 100) / 100
            if (predictionFormula > maxPrice) predictionFormula = maxPrice
            else if (predictionFormula < minPrice) predictionFormula = minPrice
            this.averagePriceArray[dateIndex] = predictionFormula
          }
        }
      }

      // Calculate average value
      let averageSum = this.averagePriceArray.reduce(
        (partialSum, a) => partialSum + a,
        0
      )
      this.averageConstant = averageSum / data.Avg.length
      this.averageConstant = Math.round(this.averageConstant * 100) / 100

      for (let i = 0; i < data.Avg.length; i++) {
        this.averageConstantArray.push(this.averageConstant)
      }

      // Sort by date so x-axis is always chronological
      const combined = this.datesPriceArray.map((date, i) => ({
        date,
        avg: this.averagePriceArray[i],
        lastYear: this.lastYearActualRentArray[i],
        constant: this.averageConstantArray[i]
      }))
      combined.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      this.datesPriceArray = combined.map((x) => x.date)
      this.averagePriceArray = combined.map((x) => x.avg)
      this.lastYearActualRentArray = combined.map((x) => x.lastYear)
      this.averageConstantArray = combined.map((x) => x.constant)

      await new Promise((r) => setTimeout(r, 500))
      if (renderId !== this._renderDataId) return
      this.fillData()
    },
    toggleChangeLogModal() {
      this.isShowChangeLogModal = !this.isShowChangeLogModal
      localStorage.setItem('currentRoomId', this.newRoomID)
    },
    async getActualRent() {
      const { startDate, endDate } = this.getPricingDateRange()
      const startStr = startDate.toLocaleDateString('en-US')
      const endStr = endDate.toLocaleDateString('en-US')
      const result = await calls.GETACTUALRENT(
        this.selectedRoom.uuid,
        startStr,
        endStr
      )
      this.actualRentArray = Array.isArray(result)
        ? result
        : (result && result.data) || []
    },
    applyRoomToForm() {
      const r = this.selectedRoom
      this.collectedData.internal_name = r.internalName
      this.collectedData.marketing_name = r.marketingName
      this.collectedData.uplistingId = r.uplistingId
      this.collectedData.code = r.code != null ? String(r.code) : ''
      this.collectedData.description = r.description
      this.collectedData.lock = r.lockType
      const savedPhotos = String(r.roomImages || r.roomImage || '')
        .split(',')
        .map((url) => url.trim())
        .filter(Boolean)
      this.existingPhotos = savedPhotos
      this.collectedData.roomImage = r.roomImage || savedPhotos[0] || ''
      const coverIndex = savedPhotos.indexOf(this.collectedData.roomImage)
      this.coverPhotoIndex = coverIndex >= 0 ? coverIndex : 0
      this.dynamicPricing = r.dynamicPricing
      this.priceStatus = r.dynamicPricing
      if (r.price != null) this.price = `$${r.price}`
      if (r.dynamicPricing) {
        this.minPrice = r.minPrice != null ? `$${r.minPrice}` : '$'
        this.maxPrice = r.maxPrice != null ? `$${r.maxPrice}` : '$'
        this.autoAdjustmentPercentage =
          r.autoAdjustmentPercentage != null
            ? `${r.autoAdjustmentPercentage}%`
            : '%'
      }
    },
    async loadRoomAdjustmentSettings() {
      const res = await calls.GET('rooms', this.selectedRoom.uuid)
      const data = res.data || res
      const room = data.room || data
      if (room && room.adjustment != null) {
        const hasPrices = room.minPrice > 0 && room.maxPrice
        this.adjustment = hasPrices ? true : room.adjustment
      }
      if (data.adjustments) this.adjustments = data.adjustments
    },
    async loadRoomPropertyAndTitle() {
      const { data } = await calls.GET('rooms/property', this.selectedRoom.uuid)
      this.collectedData.selected_property = data.uuid
      this.previously_selected_room = data.uuid
      const prop = this.properties.find(
        (p) => p.uuid === this.collectedData.selected_property
      )
      this.titlePropertyName = prop ? prop.name : null
    },
    async setLastEditDifference(roomId) {
      const roomLogs = await calls.GET('rooms/roomLastEditLogs', roomId)
      if (roomLogs.result && roomLogs.result.length > 0) {
        const lastEdit = moment(
          roomLogs.result[roomLogs.result.length - 1].dateUpdated
        )
        this.difference = moment().diff(lastEdit, 'days')
      } else {
        this.difference = 0
      }
    }
  },
  async created() {
    this.isLoadingPage = true
    this.options = this.buildHistoricalChartOptions()
    if (localStorage.getItem('currentPropertyName') != null)
      this.collectedData.selected_property =
        localStorage.getItem('currentPropertyId')
    await this.loadProperties()
    const roomId = this.$route.params.roomId
    if (roomId) {
      await this.setLastEditDifference(roomId)
      const { data } = await calls.GET('rooms', roomId)
      this.selectedRoom = data.room || data
      if (this.selectedRoom) {
        this.editing = true
        this.applyRoomToForm()
        await this.loadRoomAdjustmentSettings()
        await this.loadRoomPropertyAndTitle()
        if (this.dynamicPricing) {
          this.renderData()
          this.getRecommendedPricing()
        }
        this.documentTitle = `Rentalios | Edit ${this.titlePropertyName} ${this.collectedData.internal_name}`
      }
    }
    this.isLoadingPage = false
  },
  mounted() {
    this.fillData()
  },
  watch: {
    documentTitle(value) {
      document.title = value
    }
  }
}
</script>
<style scoped>
.FormHeader {
  height: 90px;
  background-color: #f0f8e8;
  margin: 0 0 0 0;
  padding: 0 0 0 0;
}

.FormHeader .Dot {
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  margin: 30px 0 0 0;
  border-style: solid;
  border-width: 2px;
  border-color: white;
}

.FormHeader .Dot.Green {
  background-color: #66b015;
}

.FormHeader .Dot.Grey {
  background-color: #bdbdbd;
}

.FormHeader .Line {
  display: inline-block;
  width: 148px;
  margin: 0 0 6px 0;
  padding: 0 0 0 0;
  border: none;
  height: 2px;
  background-color: #bdbdbd;
}

.FormHeader .Descriptors {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 400px;
  margin: 0 0 0 calc(50% - 200px);
}

.FormHeader .Descriptors .Label {
  font-family: Montserrat;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: center;
  height: 15px;
  width: 71px;
  left: 0px;
  top: 20px;
}

.FormHeader .Descriptors .Label.Grey {
  color: #bdbdbd;
}

.FormHeader .Descriptors .Label.Green {
  color: #66b015;
}

.container {
  margin: 24px;
  /* overflow-y: scroll; */
}

div.create-room-container {
  background-color: white;
}

.create-room-property-title {
  display: flex;
  font-size: 12px;
  margin-left: 2px;
  width: 320px;
}

.create-room-property-name {
  margin-right: 5%;
}

.create-room-description,
.pricing-container {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 25%;
  font-family: Montserrat;
}

.pricing-graph-container {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
}

.actual-rent-chart-wrapper {
  position: relative;
}

.create-room-description > * {
  font-family: Montserrat;
}

.create-room-title,
.pricing-title {
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 22px;
  color: #66b015;
}

.create-room-description-container-1 {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-room-long-label-input,
.create-room-long-label-select {
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  height: auto;
  min-height: 64px;
  width: 100%;
  padding: 0;
}

.create-room-long-label-input label,
.create-room-long-label-select label,
.create-room-small-label-input label,
.create-room-small-label-select label,
.create-room-big-label-input label,
.create-room-tiny-label-input label,
.create-room-tiny-label-select label {
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  color: #4a4a4a;
  height: 18px;
  line-height: 18px;
}

.create-room-small-label-select label,
.create-room-small-label-input label {
  display: flex;
  flex-flow: row nowrap;
  justify-content: left;
}

.create-room-long-label-input input,
.create-room-long-label-select select,
.create-room-small-label-input input,
.create-room-small-label-select select,
.create-room-big-label-input textarea,
.create-room-tiny-label-input input,
.create-room-tiny-label-select select,
.pricing-monthly-rent-input,
.pricing-min-input,
.pricing-max-input,
.pricing-auto-input {
  border: 1px solid #cbd4dc;
  box-sizing: border-box;
  border-radius: 5px;
  height: 40px;
  width: 100%;
  line-height: 48px;
  padding-left: 2%;
  padding-right: 2%;
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  display: flex;
  align-items: center;
  color: #767675;
}

.create-room-long-label-select select {
  padding-left: 1%;
}

.create-room-description-container-2 {
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 0 16px 0;
  gap: 16px;
}

.create-room-small-label-input,
.create-room-small-label-select {
  flex: 1;
}

.create-room-description-container-3 {
  width: 100%;
  padding: 16px 0 0 0;
}

.create-room-big-label-input {
  text-align: left;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
}

.create-room-big-label-input textarea {
  height: 140px;
  width: 100%;
  text-align: left;
  padding: 2%;
}

.create-room-footer-container {
  border-top: 1px solid #dfdfdf;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-around;
  padding: 24px 25%;
}

.create-room-back-button,
.create-room-next-button {
  height: 48px;
  width: 198px;
  cursor: pointer;
}

.create-room-back-button {
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  background-color: white;
  border: none;
  color: #66b015;
}

.create-room-next-button {
  border: none;
  color: white;
  background: #66b015;
  border-radius: 4px;
}

.create-room-details {
  padding: 52px 400px 300px 400px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 512px;
}

.create-room-details-container-1 {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
  width: 334px;
  height: 64px;
  padding: 24px 0 24px 0;
}

.create-room-tiny-label-input,
.create-room-tiny-label-select {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 156px;
  height: 100%;
}

.create-room-details-container-2 {
  width: 100%;
  height: 100%;
  text-align: left;
}

.create-room-details-grid-select {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: space-between;
  width: 100%;
  height: 250px;
}

.create-room-tiny-label-select {
  width: 156px;
  height: 64px;
}

.create-room-link {
  padding: 48px 350px 160px 400px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
}

.create-room-questionaire {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  height: 112px;
  width: 100%;
  padding: 32px 0 32px 0;
}

.create-room-radials-container {
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
}

.create-room-label-radial {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
}

.create-room-label-radial input {
  margin-right: 17px;
}

.create-room-images {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 25%;
  height: auto;
}

.create-room-upload-container {
  background: rgba(240, 248, 232, 0.2);
  border: 1px dashed #66b015;
  box-sizing: border-box;
  border-radius: 4px;
  width: 583px;
  height: 256px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
}

.create-room-upload-container.error {
  background: #df000f0d;
  border: 1px dashed #df000f;
}

.field-error.upload-error {
  margin-top: 8px;
}

.create-room-upload-container button {
  background: #66b015;
  border-radius: 4px;
  border: none;
  width: 176px;
  height: 40px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: #ffffff;
  margin-bottom: 8px;
}

.create-room-upload-container label {
  font-family: Montserrat;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  color: #66b015;
  margin-top: 8px;
}

input.error,
select.error {
  border-color: red;
}

.field-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e60000;
  font-size: 12px;
  line-height: 16px;
  margin-top: 0;
  font-family: Montserrat;
}

.field-error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #e60000;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  font-style: normal;
  flex-shrink: 0;
}

/* dynamic pricing styles begin here*/
.pricing-subtitle {
  font-style: normal;
  font-weight: 900;
  font-size: 13px;
  line-height: 18px;
  color: black;
}

.pricing-subtitle-dates {
  font-style: normal;
  font-size: 10px;
  line-height: 18px;
  color: black;
}

.row {
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
}

.pricing-description-text {
  font-size: 13px;
  text-align: start;
  line-height: 18px;
}

.pricing-graph {
  gap: 16px;
}

.pricing-minmax.col-2,
.pricing-monthly-rent-input {
  width: 48%;
}

.priceLine {
  display: inline-block;
  width: 100%;
  margin: 10px 0;
  padding: 0 0 0 0;
  border: none;
  height: 0.5px;
  background-color: #d8dee4;
}

.pricing-minmax-recommendation {
  color: var(--rentalios-dark);
  font-style: normal;
  font-weight: 900;
  font-size: 13px;
  line-height: 18px;
  text-align: start;
}

.pricing-minmax.col-2 p {
  text-align: start;
  font-size: 13px;
}

p.pricing-selected-property {
  font-family: Montserrat;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  align-self: center;
  flex: 1;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pricing-graph-information {
  background: #fcfcfc;
  margin-bottom: 10px;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

.pricing-graph-information img {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.pricing-description-status {
  align-self: center;
}

.status {
  cursor: pointer;
}

.pricing-graph-buttons {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}

.pricing-button {
  color: white;
  background: #66b015;
  border-radius: 3px;
  padding: 8px;
  border: none;
  font-family: Montserrat;
  font-style: normal;
  font-size: 10px;
  font-weight: bold;
  width: auto;
  white-space: nowrap;
}

.pricing-last-year-rent-button {
  background: #5987ea;
  margin-left: 8px;
}

.pricing-actual-rent-button {
  background: #0b530e;
  margin-left: 8px;
}

.pricing-average-rent-button {
  background: #9f9f9f;
  margin-left: 8px;
}

.pricing-graph-title {
  margin-bottom: 15px;
}

.pricing-seasonal-adjustment {
  width: 100%;
  margin: 8px 0 4px;
}

.room-image-wrapper {
  width: 281px;
}

.room-image-wrapper img {
  width: 100%;
}

.image-preview-container {
  margin-top: 32px;
  max-width: 583px;
  width: 100%;
  min-height: min-content;
  height: fit-content;
}

.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  width: 100%;
}

.image-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 244 / 176;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  min-height: min-content;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-btn {
  position: absolute;
  top: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  background: #4a4a4a;
  padding: 0;
}

.preview-star {
  left: 8px;
}

.preview-star.active {
  background: #66b015;
}

.preview-delete {
  right: 8px;
}

/* Hide vue2-dropzone's built-in previews. Dropzone injects this DOM
   dynamically without the component's scoped attribute, so plain scoped
   selectors never match it - a deep selector is required. */
.create-room-images ::v-deep .dz-preview,
.create-room-images ::v-deep .dz-success-mark,
.create-room-images ::v-deep .dz-error-mark {
  display: none;
}

.room-identity-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.room-identity-wrapper > * {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-weight: 600;
  color: #66b015;
  font-size: 14px;
  text-align: left;
  margin: 24px 24px 16px;
}

.modal-content {
  font-weight: 400;
  font-size: 12px;
  color: #4a4a4a;
  text-align: left;
  margin: 0 24px 16px;
}

.modal-button-wrapper {
  display: flex;
  gap: 7px;
  justify-content: flex-end;
  margin-right: 24px;
}

.back-btn {
  border: 1px solid #66b015;
  border-radius: 4px;
  color: #66b015;
  background-color: #ffffff;
  height: 40px;
  width: 120px;
}

.accept-btn {
  border: 1px solid #66b015;
  border-radius: 4px;
  color: #ffffff;
  background-color: #66b015;
  height: 40px;
  width: 120px;
}

.last-edit {
  text-decoration: underline;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.room-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pricing-change-confirmation-modal .ant-modal-body {
  padding: 8px;
}

/* Tablet: same 2-line pricing-graph-information layout */
@media screen and (max-width: 1024px) {
  .pricing-graph-information {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 16px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    align-items: center;
    text-align: left;
    justify-content: flex-start;
  }

  .pricing-graph-information img {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .pricing-graph-information .pricing-selected-property {
    flex: 0 1 auto;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .pricing-graph-buttons {
    flex-basis: 100%;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
    align-items: center;
    padding-top: 4px;
  }

  .pricing-button {
    flex: 0 1 auto;
    min-width: 0;
    padding: 6px 12px;
    font-size: 11px;
  }
}

@media screen and (max-width: 768px) {
  .container {
    margin: 16px;
  }

  /* Fix property/room/last edit title */
  .room-title-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
  }

  .create-room-property-title {
    width: 100%;
    flex-direction: column;
  }

  .create-room-property-name {
    margin-right: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .last-edit {
    align-self: flex-end;
    font-size: 14px;
    font-weight: 500;
  }

  .FormHeader .Descriptors {
    display: unset;
    flex-flow: unset;
    justify-content: unset;
    width: 100%;
    margin: 0;
  }

  .description {
    float: left;
  }

  .pricing {
    float: right;
  }

  /* Fix step 1 form - make it row form */
  .create-room-description {
    padding: 20px;
    margin-top: 0;
  }

  .create-room-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 20px;
  }

  .create-room-description-container-1 {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .room-identity-wrapper {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .create-room-long-label-input {
    width: 100%;
  }

  .create-room-long-label-input label,
  .create-room-long-label-select label,
  .create-room-small-label-input label,
  .create-room-small-label-select label,
  .create-room-big-label-input label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .create-room-long-label-input input,
  .create-room-long-label-select select,
  .create-room-small-label-input input,
  .create-room-small-label-select select,
  .create-room-big-label-input textarea {
    height: 44px;
    font-size: 13px;
    padding: 10px 14px;
  }

  .create-room-description-container-2 {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .create-room-small-label-input,
  .create-room-small-label-select {
    width: 100%;
  }

  .create-room-big-label-input textarea {
    height: 100px;
    font-size: 13px;
    padding: 10px 14px;
  }

  /* Fix step 2 form - re-center upload image */
  .create-room-images {
    align-items: center;
    padding: 20px;
  }

  .create-room-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 6px;
  }

  .create-room-subtitle {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;
  }

  .create-room-upload-container {
    width: 100%;
    max-width: 400px;
    height: 200px;
    margin: 0 auto;
  }

  .create-room-upload-container button {
    width: 140px;
    height: 40px;
    font-size: 13px;
    font-weight: 600;
  }

  .create-room-upload-container label {
    font-size: 12px;
    font-weight: 500;
  }

  .image-preview-container {
    justify-content: center;
    margin-top: 20px;
  }

  /* Fix step 3 form - add padding and make row form */
  .pricing-container {
    align-items: stretch;
    padding: 20px;
    margin-top: 0;
  }

  .pricing-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 20px;
  }

  .pricing-subtitle {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .pricing-description-text {
    font-size: 13px;
    line-height: 18px;
    margin-bottom: 14px;
  }

  .pricing-graph-container {
    padding: 0;
  }

  .pricing-graph {
    flex-direction: column;
    gap: 20px;
  }

  .pricing-minmax.col-2 {
    width: 100%;
  }

  .pricing-minmax.col-2 p {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  .pricing-min-input,
  .pricing-max-input,
  .pricing-auto-input {
    height: 44px;
    font-size: 13px;
    padding: 10px 14px;
  }

  .pricing-minmax-recommendation {
    font-size: 12px;
    font-weight: 600;
    margin-top: 6px;
  }

  /* 2-line layout: line 1 = icon + room name, line 2 = buttons */
  .pricing-graph-information {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 16px;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    align-items: center;
    text-align: left;
    justify-content: flex-start;
  }

  .pricing-graph-information img {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin: 0;
  }

  .pricing-graph-information .pricing-selected-property {
    flex: 0 1 auto;
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .pricing-graph-buttons {
    flex-basis: 100%;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
    padding-top: 2px;
  }

  .pricing-button {
    flex: 0 1 auto;
    min-width: 0;
    height: 32px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 600;
  }

  .pricing-selected-property {
    width: auto;
    position: static;
    display: block;
  }

  .pricing-subtitle-dates {
    font-size: 12px;
    text-align: center;
    font-weight: 500;
  }

  .pricing-graph-title {
    text-align: center;
    justify-content: center;
  }

  .pricing-graph-title.row {
    justify-content: center;
    gap: 55px;
  }

  .pricing-graph-container > div:last-child {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  /* Footer adjustments */
  .create-room-footer-container {
    padding: 20px;
    flex-direction: column;
    gap: 16px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
  }

  .create-room-back-button,
  .create-room-next-button {
    width: 95%;
    height: 48px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
  }

  .create-room-back-button {
    background: white;
    border: 2px solid #66b015;
    color: #66b015;
  }

  .create-room-next-button {
    background: #66b015;
    border: 2px solid #66b015;
    color: white;
  }

  /* Container adjustments */
  div.create-room-container {
    overflow-x: hidden;
    padding-top: 0;
    height: auto;
    min-height: 100vh;
    background: #f8f9fa;
  }

  /* Additional mobile improvements */
  .priceLine {
    margin: 16px 0;
  }

  .pricing-description-status {
    align-self: center;
  }

  .status {
    width: 50px;
    height: 24px;
    top: -2px;
    position: relative;
  }

  /* Modal improvements for mobile */
  .modal-title {
    font-size: 16px;
    font-weight: 700;
  }

  .modal-content {
    font-size: 14px;
    line-height: 20px;
  }

  .back-btn,
  .accept-btn {
    height: 44px;
    font-size: 14px;
    font-weight: 600;
  }

  .create-room-long-label-input input,
  .create-room-long-label-select select,
  .create-room-small-label-input input,
  .create-room-small-label-select select,
  .create-room-tiny-label-input input,
  .create-room-tiny-label-select select,
  .pricing-monthly-rent-input,
  .pricing-min-input,
  .pricing-max-input,
  .pricing-auto-input {
    width: 100%;
  }

  .create-room-big-label-input textarea {
    width: 90%;
  }
}
</style>
